import React from 'react';
import { Box, Chip, Typography, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import DiffLine from './DiffLine';
import CommentItem from '../Comments/CommentItem';
import parseCommentText from '../Comments/parseCommentText';

const isCommentUnfixed = (comment, stagedIntentionalTids) => {
  const body = (comment && comment.body ? String(comment.body) : '').toLowerCase();
  const tidRaw = comment && (comment.thread_id ?? comment.threadId ?? comment.ThreadID);
  const tid = typeof tidRaw === 'string' ? parseInt(tidRaw, 10) : tidRaw;
  const isStaged = Number.isFinite(tid) && stagedIntentionalTids.has(tid);
  return !body.includes('fixed - ') && !body.includes('marked intentional - ') && !isStaged;
};

const renderCommentBody = (c) => {
  let txt = '';
  if (typeof c === 'string') txt = c;
  else if (c && typeof c === 'object') {
    txt = c.body || c.Body || c.content || c.Content || c.text || c.message || c.comment || c.description || '';
    if (!txt) {
      const stringFields = Object.entries(c)
        .filter(([_, v]) => typeof v === 'string' && v.length > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n');
      txt = stringFields || JSON.stringify(c, null, 2);
    }
  } else txt = String(c || '');
  return parseCommentText(txt);
};

export default function FileDiffPanel({ file, isPRActive, appliedFixes = {}, intentionalStaged = {}, onFixFile, onDiscardApplied, onViewAppliedFix, fixingFilePath = '', onFixRequest, onMarkIntentional, onResolveComment, provider = 'github', buildAfterFixLineSet, buildIntentionalLineSet, selectedFilePath }) {
  const filePath = (file && (file.path || file.filename)) || '';
  const stagedSetForHeader = React.useMemo(() => {
    const raw = Array.isArray(intentionalStaged[filePath]) ? intentionalStaged[filePath] : [];
    const ids = raw.map(v => (typeof v === 'string' ? parseInt(v, 10) : v)).filter(Number.isFinite);
    return new Set(ids);
  }, [intentionalStaged, filePath]);
  const hasUnfixed = React.useMemo(() => {
    if (!file || !Array.isArray(file.comments)) return false;
    return file.comments.some(c => isCommentUnfixed(c, stagedSetForHeader));
  }, [file ? file.comments : [], stagedSetForHeader]);
  if (!file) return (<Typography sx={{ color: '#7d8590' }}>Select a file to view changes.</Typography>);
  const hasComments = Array.isArray(file.comments) && file.comments.length > 0;
  const isAllFixed = hasComments && file.comments.every(c => (c.body || '').toString().toLowerCase().includes('fixed - '));
  const hasIntentionalCommitted = Array.isArray(file.comments) && file.comments.some(c => (c.body || '').toString().toLowerCase().includes('marked intentional - '));
  const showGreen = isAllFixed || (hasIntentionalCommitted && !hasUnfixed);

  const afterFixLineSet = buildAfterFixLineSet(file.comments || []);
  const intentionalLineSet = buildIntentionalLineSet(file.comments || []);

  return (
    <>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0d1117', borderBottom: '1px solid #30363d', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f0f6fc', flex: 1 }}>{file.path || file.filename}</Typography>
        <Chip label={`${file.diff_lines?.length || 0} changes`} size="small" sx={{ backgroundColor: '#21262d', color: '#7d8590', fontSize: '12px' }} />
        <Chip label={`issues: ${file.comments?.length || 0}`} size="small" sx={{ backgroundColor: '#21262d', color: '#7d8590', fontSize: '12px' }} />
        <Chip label={`fixed: ${(file.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('fixed - ')).length}`} size="small" sx={{ backgroundColor: '#21262d', color: '#7d8590', fontSize: '12px' }} />
        <Chip label={`intentional: ${(file.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('marked intentional - ')).length}`} size="small" sx={{ backgroundColor: '#21262d', color: '#ffa657', fontSize: '12px' }} />
        <Chip label={`intentional (staged): ${(() => { const arr = intentionalStaged[(file.path || file.filename)] || []; return Array.isArray(arr) ? arr.length : 0; })()}`} size="small" sx={{ backgroundColor: '#21262d', color: '#ffa657', fontSize: '12px' }} />
        {showGreen && (
          <Chip label="Fixed File" size="small" sx={{ ml: 0.5, backgroundColor: '#122e1b', color: '#7ee787', border: '1px solid #238636', fontWeight: 700 }} />
        )}
        {(() => {
          const fp = (file.path || file.filename);
          const stagedEntry = appliedFixes[fp];
          const tokens = stagedEntry ? (Array.isArray(stagedEntry.tokens) ? stagedEntry.tokens : (stagedEntry.token ? [stagedEntry.token] : [])) : [];
          const hasTokens = tokens.length > 0;
          const hasIntentionalOnly = !hasTokens && Array.isArray(intentionalStaged[fp]) && intentionalStaged[fp].length > 0;
          if (hasTokens) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {typeof onViewAppliedFix === 'function' && (
                  <Button size="small" onClick={() => onViewAppliedFix(fp)} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>View applied fixes</Button>
                )}
                <Button size="small" onClick={() => onDiscardApplied(fp)} sx={{ color: '#f85149', '&:hover': { backgroundColor: '#f8514920' } }}>Discard</Button>
              </Box>
            );
          }
          if (hasIntentionalOnly) {
            const hasUnfixedLocal = hasUnfixed;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onFixFile(file)}
                  startIcon={<BuildIcon />}
                  disabled={!hasUnfixedLocal || fixingFilePath === fp || !isPRActive}
                  sx={{
                    ml: 0,
                    color: (!hasUnfixedLocal || !isPRActive) ? '#f85149' : (fixingFilePath === fp ? '#0d1117' : '#58a6ff'),
                    borderColor: (!hasUnfixedLocal || !isPRActive) ? '#f85149' : (fixingFilePath === fp ? '#2ea043' : '#58a6ff'),
                    backgroundColor: fixingFilePath === fp ? '#3fb950' : 'transparent',
                    '&:hover': { backgroundColor: fixingFilePath === fp ? '#2ea043' : ((!hasUnfixedLocal || !isPRActive) ? '#f8514920' : '#58a6ff20') },
                    '&.Mui-disabled': { color: '#f85149', borderColor: '#f85149', backgroundColor: '#f8514920' },
                  }}
                >
                  {fixingFilePath === fp ? 'Generating…' : 'Fix File'}
                </Button>
                <Button size="small" onClick={() => onDiscardApplied(fp)} sx={{ color: '#f85149', '&:hover': { backgroundColor: '#f8514920' } }}>Discard</Button>
              </Box>
            );
          }
          if (!appliedFixes[fp] && !(Array.isArray(intentionalStaged[fp]) && intentionalStaged[fp].length > 0)) {
            return (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onFixFile(file)}
                startIcon={<BuildIcon />}
                disabled={!hasUnfixed || !isPRActive}
                sx={{
                  ml: 2,
                  color: (!hasUnfixed || !isPRActive) ? '#f85149' : '#58a6ff',
                  borderColor: (!hasUnfixed || !isPRActive) ? '#f85149' : '#58a6ff',
                  '&:hover': { backgroundColor: (!hasUnfixed || !isPRActive) ? '#f8514920' : '#58a6ff20' },
                  '&.Mui-disabled': { color: '#f85149', borderColor: '#f85149', backgroundColor: '#f8514920' },
                }}
              >
                Fix File
              </Button>
            );
          }
          return null;
        })()}
      </Box>

      {Array.isArray(file.comments) && file.comments.some(c => (c.body || '').toString().toLowerCase().includes('fixed - ')) && (
        <Accordion sx={{ backgroundColor: '#0d1117', border: '1px solid #30363d', m: 3, mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#7d8590' }} />} sx={{ backgroundColor: '#161b22' }}>
            <Typography sx={{ color: '#7d8590', fontWeight: 600 }}>Show fixed comments</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(file.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('fixed - ')).map((comment, idx) => (
              <CommentItem
                key={`fixed-${idx}`}
                comment={comment}
                onFixRequest={() => {}}
                onMarkIntentional={() => {}}
                line={{}}
                disableFix={true}
                renderBody={renderCommentBody}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {Array.isArray(file.comments) && file.comments.some(c => (c.body || '').toString().toLowerCase().includes('marked intentional - ')) && (
        <Accordion sx={{ backgroundColor: '#0d1117', border: '1px solid #30363d', m: 3, mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#7d8590' }} />} sx={{ backgroundColor: '#161b22' }}>
            <Typography sx={{ color: '#ffa657', fontWeight: 700 }}>Show intentional comments</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(file.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('marked intentional - ')).map((comment, idx) => (
              <CommentItem
                key={`intent-${idx}`}
                comment={comment}
                onFixRequest={() => {}}
                onMarkIntentional={() => {}}
                line={{}}
                disableFix={true}
                renderBody={renderCommentBody}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {file.diff_lines && file.diff_lines.length > 0 && (
        <Box sx={{ px: 3, pb: 3 }}>
          {file.diff_lines.map((line, index) => {
            const lineCommentsAll = file.comments || [];
            const rawContent = (line && typeof line.content === 'string') ? line.content : '';
            const headerCheck = rawContent.replace(/^[+\-\s\t]/, '').trim();
            if (/^a\/.+\sb\/.+$/i.test(headerCheck) || /^new file mode\b/i.test(headerCheck) || /^deleted file mode\b/i.test(headerCheck) || /^index\s+[0-9a-f]+\.[0-9a-f]+/i.test(headerCheck) || /^\\\s*No newline at end of file$/i.test(headerCheck) || /^No newline at end of file$/i.test(headerCheck)) return null;
            const firstChar = rawContent.charAt(0);
            let normalized = rawContent;
            if (firstChar === '+' || firstChar === ' ' || firstChar === '\\t') normalized = rawContent.slice(1).trim();
            else if (firstChar === '-') normalized = '';
            else normalized = rawContent.trim();

            let contentMatch = false;
            if (afterFixLineSet.size > 0 && normalized) { const normCollapsed = normalized.replace(/\s+/g, ' ').trim(); contentMatch = afterFixLineSet.has(normalized) || afterFixLineSet.has(normCollapsed); }
            const diffLineNum = typeof line.line_number === 'string' ? parseInt(line.line_number, 10) : line.line_number;
            const lineMatch = lineCommentsAll.some(c => { const hasFixed = (c.body || '').toString().toLowerCase().includes('fixed - '); const cLine = typeof c.line === 'string' ? parseInt(c.line, 10) : c.line; return hasFixed && cLine === diffLineNum; });
            const isRemoved = String(line && line.type ? line.type : '').toLowerCase() === 'removed';
            const fixedLine = (!isRemoved) && (contentMatch || lineMatch);
            const lineComments = isRemoved ? [] : (lineCommentsAll.filter(c => { const commentLine = typeof c.line === 'string' ? parseInt(c.line, 10) : c.line; const diffLine = typeof line.line_number === 'string' ? parseInt(line.line_number, 10) : line.line_number; const isFixed = (c.body || '').toString().toLowerCase().includes('fixed - '); const isIntentionalCommitted = (c.body || '').toString().toLowerCase().includes('marked intentional - '); return commentLine === diffLine && !isFixed && !isIntentionalCommitted; }) || []);
            const staged = new Set(((appliedFixes[(file.path || file.filename)] || {}).commentThreadsApplied || []).map(v => (typeof v === 'string' ? parseInt(v, 10) : v)).filter(Number.isFinite));
            const stagedIntentional = new Set((intentionalStaged[(file.path || file.filename)] || []).map(v => (typeof v === 'string' ? parseInt(v, 10) : v)).filter(Number.isFinite));
            let intentionalContentMatch = false;
            if (intentionalLineSet.size > 0 && normalized) { const normCollapsed2 = normalized.replace(/\s+/g, ' ').trim(); intentionalContentMatch = intentionalLineSet.has(normalized) || intentionalLineSet.has(normCollapsed2); }
            const intentionalResolved = intentionalContentMatch;
            return (
              <DiffLine
                key={index}
                line={{ ...line, file_path: file.path || file.filename }}
                comments={lineComments}
                onFixRequest={onFixRequest}
                onMarkIntentional={onMarkIntentional}
                fixedLine={fixedLine}
                stagedIds={staged}
                stagedIntentionalIds={stagedIntentional}
                intentionalLine={intentionalResolved}
                canFix={isPRActive}
                renderCommentItem={({ comment, onFixRequest, onMarkIntentional, line, disableFix, fixApplied, intentionalStaged }) => (
                  <CommentItem
                    comment={comment}
                    onFixRequest={onFixRequest}
                    onMarkIntentional={onMarkIntentional}
                    onResolve={provider === 'azure' ? undefined : onResolveComment}
                    provider={provider}
                    line={line}
                    disableFix={disableFix}
                    fixApplied={fixApplied}
                    intentionalStaged={intentionalStaged}
                    renderBody={renderCommentBody}
                  />
                )}
              />
            );
          })}
        </Box>
      )}
    </>
  );
}


